import { apiRequest } from '@/shared/lib/api-client';
import type { AuthUser } from '@/shared/stores/auth-store';
import { type AcessoRaw, acessoDaApi } from './perfil-de-acesso';

interface LoginResponseRaw {
  access_token: string;
  expires_in: number;
  impersonating?: { email: string };
  user: {
    id: number;
    tenant_id: number;
    email: string;
    name: string;
    profile: 'admin' | 'attendant';
    department_ids?: number[];
    access_group_ids?: number[];
    /** Ausente em API antiga (antes dos perfis) → tratado como só Atendimento. */
    acesso?: AcessoRaw | null;
  };
}

interface SupportTotpRequiredResponse {
  status: 'support_totp_required';
  challenge_token: string;
  /** Qual caminho o SERVIDOR escolheu. Ele sabe; a tela não adivinha. */
  via?: 'super' | 'local';
}

/** Computador confiável: a senha bateu e o código foi dispensado (24h). */
interface SupportHomologatedResponse {
  status: 'support_homologated';
  via: 'super';
  homologated_token: string;
  tenants: SuperTenant[];
  unavailable_servers?: UnavailableServer[];
}

interface SupportSelectTenantResponse {
  status: 'select_tenant';
  support_pending_token: string;
  support_email: string;
  tenants: Array<{
    id: number;
    name: string;
    slug: string;
    status: 'active' | 'suspended' | 'trial';
  }>;
}

interface RefreshResponseRaw {
  access_token: string;
  expires_in: number;
  impersonating?: { email: string };
  user: {
    id: number;
    tenant_id: number;
    email: string;
    name: string;
    profile: 'admin' | 'attendant';
    department_ids?: number[];
    access_group_ids?: number[];
    /** Ausente em API antiga (antes dos perfis) → tratado como só Atendimento. */
    acesso?: AcessoRaw | null;
  };
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface LoginResult {
  accessToken: string;
  expiresIn: number;
  user: AuthUser;
}

export type LoginOutcome =
  | { kind: 'session'; result: LoginResult }
  | {
      kind: 'support_totp_required';
      challengeToken: string;
      /**
       * 'super'  = homologado no painel; a lista traz as empresas de TODOS os servidores
       * 'local'  = credencial replicada neste servidor (o caminho antigo)
       *
       * Quem decide é o servidor (ele sabe se está configurado). Ausente = 'local',
       * pra funcionar contra uma API que ainda não manda o campo.
       */
      via: 'super' | 'local';
    }
  | {
      /** Este computador digitou o código nas últimas 24h — vai direto pra lista. */
      kind: 'support_homologated';
      result: SuperTotpResult;
    };

export async function loginRequest(input: LoginInput): Promise<LoginOutcome> {
  const data = await apiRequest<
    LoginResponseRaw | SupportTotpRequiredResponse | SupportHomologatedResponse
  >('/auth/login', {
    method: 'POST',
    body: input,
    skipAuth: true,
  });
  if ('status' in data && data.status === 'support_homologated') {
    return {
      kind: 'support_homologated',
      result: {
        homologatedToken: data.homologated_token,
        tenants: data.tenants,
        unavailableServers: data.unavailable_servers ?? [],
      },
    };
  }
  if ('status' in data && data.status === 'support_totp_required') {
    return {
      kind: 'support_totp_required',
      challengeToken: data.challenge_token,
      via: data.via ?? 'local',
    };
  }
  const session = data as LoginResponseRaw;
  return {
    kind: 'session',
    result: {
      accessToken: session.access_token,
      expiresIn: session.expires_in,
      user: {
        id: session.user.id,
        tenantId: session.user.tenant_id,
        email: session.user.email,
        name: session.user.name,
        profile: session.user.profile,
        departmentIds: session.user.department_ids ?? [],
        accessGroupIds: session.user.access_group_ids ?? [],
        acesso: acessoDaApi(session.user.acesso),
        ...(session.impersonating && { impersonating: session.impersonating }),
      },
    },
  };
}

export interface SupportTotpResult {
  supportPendingToken: string;
  supportEmail: string;
  tenants: SupportSelectTenantResponse['tenants'];
}

export async function supportLoginTotpRequest(
  challengeToken: string,
  code: string,
): Promise<SupportTotpResult> {
  const data = await apiRequest<SupportSelectTenantResponse>('/auth/support/login/totp', {
    method: 'POST',
    body: { challenge_token: challengeToken, code },
    skipAuth: true,
  });
  return {
    supportPendingToken: data.support_pending_token,
    supportEmail: data.support_email,
    tenants: data.tenants,
  };
}

export async function supportSelectTenantRequest(
  supportPendingToken: string,
  tenantId: number,
): Promise<LoginResult> {
  const data = await apiRequest<LoginResponseRaw>('/auth/support/select-tenant', {
    method: 'POST',
    body: { support_pending_token: supportPendingToken, tenant_id: tenantId },
    skipAuth: true,
  });
  return {
    accessToken: data.access_token,
    expiresIn: data.expires_in,
    user: {
      id: data.user.id,
      tenantId: data.user.tenant_id,
      email: data.user.email,
      name: data.user.name,
      profile: data.user.profile,
      departmentIds: data.user.department_ids ?? [],
      accessGroupIds: data.user.access_group_ids ?? [],
      acesso: acessoDaApi(data.user.acesso),
      ...(data.impersonating && { impersonating: data.impersonating }),
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Homologação central (super-painel).
//
// Espelha as três funções acima de propósito: o formato do retorno é o mesmo, pra
// que a tela troque de caminho sem se reescrever. A diferença que aparece pro
// usuário é uma só — a lista traz empresas de TODOS os servidores, e diz quais
// servidores não responderam.
//
// Estas rotas só existem se o servidor foi configurado pra isso. Se não existirem,
// a API responde 404 e o fluxo antigo (acima) segue valendo.
// ─────────────────────────────────────────────────────────────────────────────

export interface SuperTenant {
  id: number;
  name: string;
  slug: string;
  status: string;
  /** Em qual servidor a empresa mora. Obrigatório na escolha: os IDs COLIDEM
   *  entre servidores (cada banco tem sua sequência), então `id` sozinho é
   *  ambíguo — existe "empresa 5" em mais de um lugar. */
  server_id: number;
  server_name: string;
}

/** Servidor que não respondeu ao montar a lista. Vai pra TELA, não pro log. */
export interface UnavailableServer {
  server_id: number;
  server_name: string;
  reason: string;
}

export interface SuperTotpResult {
  homologatedToken: string;
  tenants: SuperTenant[];
  /** Sem isto, a pessoa procuraria a empresa, não acharia, e concluiria que
   *  ela sumiu. */
  unavailableServers: UnavailableServer[];
}

export async function superSupportTotpRequest(
  challengeToken: string,
  code: string,
  /** "Não pedir o código neste computador por 24h" — a API guarda num cookie httpOnly. */
  lembrar: boolean,
): Promise<SuperTotpResult> {
  const data = await apiRequest<{
    homologated_token: string;
    tenants: SuperTenant[];
    unavailable_servers: UnavailableServer[];
  }>('/auth/support/super/totp', {
    method: 'POST',
    body: { challenge_token: challengeToken, code, lembrar },
    skipAuth: true,
  });
  return {
    homologatedToken: data.homologated_token,
    tenants: data.tenants,
    unavailableServers: data.unavailable_servers ?? [],
  };
}

/**
 * Escolher a empresa são DUAS chamadas, e a separação não é capricho:
 *
 *   1. `authorize` → este servidor pede a autorização ao painel
 *   2. `enter`     → o gateway roteia pro servidor DONO da empresa, lendo a
 *                    audiência do próprio token
 *
 * A empresa escolhida pode não morar no servidor onde você homologou. Por isso a
 * autorização volta pro navegador em vez de ser consumida no passo 1.
 */
export async function superSupportEnterRequest(
  homologatedToken: string,
  serverId: number,
  tenantId: number,
): Promise<LoginResult> {
  const auth = await apiRequest<{ entry_token: string }>('/auth/support/super/authorize', {
    method: 'POST',
    body: { homologated_token: homologatedToken, server_id: serverId, tenant_id: tenantId },
    skipAuth: true,
  });

  // O `tenant_id` NÃO vai aqui de propósito: ele está dentro do token, e é de lá
  // que o servidor lê. Mandar no corpo não teria efeito — e o backend recusa o
  // campo justamente pra ninguém achar que teria.
  const data = await apiRequest<LoginResponseRaw>('/auth/support/enter', {
    method: 'POST',
    body: { entry_token: auth.entry_token },
    skipAuth: true,
  });
  return {
    accessToken: data.access_token,
    expiresIn: data.expires_in,
    user: {
      id: data.user.id,
      tenantId: data.user.tenant_id,
      email: data.user.email,
      name: data.user.name,
      profile: data.user.profile,
      departmentIds: data.user.department_ids ?? [],
      accessGroupIds: data.user.access_group_ids ?? [],
      acesso: acessoDaApi(data.user.acesso),
      ...(data.impersonating && { impersonating: data.impersonating }),
    },
  };
}

/** Timeout client-side pra refresh: se backend demora >5s, assume falha
 *  e força redirect pra /login. Sem isso, Redis off na API faz refresh
 *  pendurar até 30s (commandTimeout do ioredis) e UI fica em tela branca. */
const REFRESH_TIMEOUT_MS = 5000;

export async function refreshRequest(): Promise<{
  accessToken: string;
  expiresIn: number;
  user: AuthUser;
}> {
  const data = await Promise.race([
    apiRequest<RefreshResponseRaw>('/auth/refresh', {
      method: 'POST',
      body: {},
      skipAuth: true,
    }),
    new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error(`refresh timeout after ${REFRESH_TIMEOUT_MS}ms`)),
        REFRESH_TIMEOUT_MS,
      ),
    ),
  ]);
  return {
    accessToken: data.access_token,
    expiresIn: data.expires_in,
    user: {
      id: data.user.id,
      tenantId: data.user.tenant_id,
      email: data.user.email,
      name: data.user.name,
      profile: data.user.profile,
      departmentIds: data.user.department_ids ?? [],
      accessGroupIds: data.user.access_group_ids ?? [],
      acesso: acessoDaApi(data.user.acesso),
      ...(data.impersonating && { impersonating: data.impersonating }),
    },
  };
}

// Logout PER-DEVICE — revoga apenas o refresh token desse browser (cookie httpOnly).
// Outros devices (mobile, outros browsers) permanecem logados. Não emite
// `auth.session_revoked` — abas do MESMO browser caem juntas via cookie shared.
export async function logoutRequest(): Promise<void> {
  await apiRequest('/auth/logout', { method: 'POST', body: {} });
}

// Auto-serviço pra "sair de todos os dispositivos" — caso "perdi o celular",
// suspeita de invasão, etc. Bumpa tokenVersion + revoga TODOS refresh tokens
// no Redis + emite `auth.session_revoked` (web + mobile + outros browsers
// caem em ms). Botão "Sair de todos" no menu user.
export async function revokeAllSessionsRequest(): Promise<void> {
  await apiRequest('/me/revoke-all-sessions', { method: 'POST', body: {} });
}

// Troca da própria senha (só a nova). Não derruba a sessão atual.
export async function changePasswordRequest(input: {
  current_password: string;
  new_password: string;
}): Promise<void> {
  await apiRequest('/me/password', { method: 'POST', body: input });
}

/** Telas/permissões do usuário atual (perfil de acesso) — GET /me/acesso. */
export async function buscarMeuAcesso() {
  return acessoDaApi(await apiRequest<AcessoRaw>('/me/acesso'));
}
